import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MonoLabel, Em } from "@/site/components/Atoms";

/* === ANCHORS === */
const NAV = [
  { id: "legal-notice", label: "Legal Notice" },
  { id: "company", label: "Company Information" },
  { id: "terms", label: "Terms of Use" },
  { id: "privacy", label: "Privacy Policy" },
  { id: "cookies", label: "Cookie Policy" },
  { id: "disclaimer", label: "Disclaimer" },
  { id: "partners", label: "Partner & Referral Disclosure" },
  { id: "contact-form", label: "Contact Form Notice" },
  { id: "marketing", label: "Marketing Consent" },
  { id: "security", label: "Security" },
  { id: "accessibility", label: "Accessibility" },
];

const LEGAL_EMAIL = "legal@forealco.com";

export default function Legal() {
  const [active, setActive] = useState(NAV[0].id);

  useEffect(() => {
    const sections = NAV.map((n) => document.getElementById(n.id)).filter(Boolean);
    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting).sort(
          (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
        );
        if (visible[0]) setActive(visible[0].target.id);
      },
      { rootMargin: "-100px 0px -65% 0px", threshold: [0, 0.5, 1] }
    );
    sections.forEach((s) => obs.observe(s));
    return () => obs.disconnect();
  }, []);

  return (
    <>
      <Hero />

      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-16 grid grid-cols-12 gap-10">
        {/* sticky TOC */}
        <aside className="col-span-12 lg:col-span-3">
          <div className="lg:sticky lg:top-24">
            <MonoLabel>ON THIS PAGE</MonoLabel>
            <ul className="mt-5 space-y-1.5 border-l border-white/10">
              {NAV.map((n) => (
                <li key={n.id}>
                  <a
                    href={`#${n.id}`}
                    data-testid={`legal-toc-${n.id}`}
                    className={`block pl-4 -ml-px py-1.5 text-[12px] leading-[1.45] border-l transition-all ${
                      active === n.id
                        ? "border-[#9DB4FF] text-white"
                        : "border-transparent text-white/55 hover:text-white"
                    }`}
                  >
                    {n.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* content */}
        <div className="col-span-12 lg:col-span-9 space-y-20">
          <Section id="legal-notice" n="01" title="Legal Notice">
            <p>
              Forealco is a business information, market-entry, advisory coordination, research, and
              project development brand operated within the Erenown ecosystem.
            </p>
            <p>
              The content published on this website is provided for general informational, business
              development, market-entry, and ecosystem coordination purposes. It may include
              information regarding Kosovo, market opportunities, institutions, business setup
              routes, advisory support, investment context, procurement context, local ecosystem
              references, partner networks, and related commercial opportunities.
            </p>
            <p>
              Unless expressly stated otherwise, the information on this website does not constitute
              legal, tax, accounting, investment, immigration, financial, public procurement,
              regulatory, or professional advice. Users should seek advice from qualified
              professionals before making any decision based on the information provided on this
              website.
            </p>
            <p>
              Forealco may prepare paid research notes, market-entry assessments, project briefs,
              business development reports, opportunity analyses, partner mapping documents,
              institutional coordination files, or similar project-based deliverables where
              separately agreed in writing.
            </p>
            <p>
              Forealco may also coordinate introductions, referrals, or business development
              opportunities with selected professional partners, technology providers, advisory
              firms, banks, institutions, investors, suppliers, or other ecosystem participants. In
              some cases, Forealco or the relevant Erenown company may receive a referral fee,
              coordination fee, success fee, commission, or other commercial benefit from the
              seller, provider, partner, or third-party side.
            </p>
            <p>
              Unless expressly agreed otherwise, Forealco does not charge users merely for general
              website access, general informational content, or standard company setup information
              published on the website.
            </p>
            <p>
              Forealco does not guarantee that any market-entry process, project preparation,
              business setup, institutional communication, bank discussion, investment discussion,
              procurement opportunity, supplier introduction, partnership discussion, or referral
              process will result in approval, registration, financing, contract award, investment,
              commercial success, or any specific outcome.
            </p>
          </Section>

          <Section id="company" n="02" title="Company Information">
            <SubBlock title="Brand">
              Forealco is a business information and advisory coordination brand by Erenown.
            </SubBlock>

            <SubBlock title="Primary website operator">
              <strong className="text-white/90">Erenown LLC</strong>
              <br />Bulevardi Dëshmorët e Kombit, C2, HIII, Nr.6
              <br />Prishtinë, Republic of Kosovo
              <br />Registration number: 811869512
            </SubBlock>

            <SubBlock title="Separate UK company under same ultimate beneficial ownership and control">
              <strong className="text-white/90">Erenown Ltd</strong>
              <br />83 St. Marks Road, Enfield, England, EN1 1BJ
              <br />United Kingdom
              <br />Company number: 12845592
            </SubBlock>

            <SubBlock title="General contact">
              <a href={`mailto:${LEGAL_EMAIL}`} className="text-[#9DB4FF] hover:text-white">
                {LEGAL_EMAIL}
              </a>
            </SubBlock>

            <SubBlock title="Status note">
              Erenown LLC and Erenown Ltd are separate companies under the same ultimate beneficial
              ownership and control. Publication of both entities on this website does not mean that
              one is a branch, subsidiary, representative office, or office of the other unless
              expressly stated in a specific contract or legal instrument.
            </SubBlock>

            <SubBlock title="Operating note">
              Depending on geography, service scope, business model, opportunity structure, and
              contract allocation, a specific service may be offered by Erenown LLC, Erenown Ltd, or
              another company under the same ultimate beneficial ownership and control, provided
              the relevant contracting entity is clearly identified.
            </SubBlock>
          </Section>

          <Section id="terms" n="03" title="Terms of Use">
            <p>
              These Terms of Use govern the use of the Forealco website. By accessing or using this
              website, you agree to these Terms of Use. If you do not agree with these terms, please
              do not use the website.
            </p>

            <SubBlock title="3.1 Website Purpose">
              This website presents information about Forealco's business focus, Kosovo-related
              market-entry perspective, advisory coordination model, research capability, project
              development approach, institutional references, ecosystem model, articles, resources,
              and related business development content. The website may include articles, market
              explanations, institutional references, business environment notes, external links,
              service descriptions, and general market information.
            </SubBlock>

            <SubBlock title="3.2 No Professional Advice">
              Website content is provided for general information only. It does not constitute
              legal, tax, accounting, regulatory, immigration, investment, financial, public
              procurement, banking, or other professional advice. Users should obtain qualified
              professional advice before making decisions based on information published on this
              website.
            </SubBlock>

            <SubBlock title="3.3 Paid Research, Project Preparation, and Advisory Coordination">
              <p>
                Forealco may provide paid services where separately agreed in writing. These may
                include, but are not limited to:
              </p>
              <Ul items={[
                "market-entry research",
                "Kosovo business environment assessments",
                "partner or supplier mapping",
                "institutional opportunity analysis",
                "project brief preparation",
                "business development reports",
                "procurement opportunity screening",
                "investment context notes",
                "vendor or buyer coordination",
                "advisory coordination",
                "commercial opportunity structuring",
                "ecosystem mapping",
                "cross-border business development support",
              ]} />
              <p>
                Any such paid work must be separately agreed through a proposal, written
                confirmation, statement of work, engagement letter, or contract. Use of the website,
                reading articles, submitting a form, or requesting general information does not by
                itself create a paid engagement.
              </p>
            </SubBlock>

            <SubBlock title="3.4 No Guarantee of Results">
              <p>
                Descriptions of Kosovo market-entry support, business setup coordination, advisory
                pathways, partner introductions, institutional communication, investment discussions,
                procurement opportunities, supplier contacts, or ecosystem references are indicative
                only. Actual outcomes depend on facts, applicable jurisdiction, institutional
                requirements, partner availability, regulatory procedures, commercial readiness,
                technical readiness, and separate agreements.
              </p>
              <p>Forealco does not guarantee:</p>
              <Ul items={[
                "company registration approval",
                "bank account opening",
                "licensing or permit approval",
                "immigration, residence, or work permit approval",
                "institutional acceptance",
                "financing or investment success",
                "public procurement eligibility or award",
                "tax, legal, or regulatory outcomes",
                "commercial partnership success",
                "supplier acceptance",
                "buyer acceptance",
                "market performance or profitability",
              ]} />
            </SubBlock>

            <SubBlock title="3.5 Third-Party Services and Partners">
              Forealco may work with or introduce users to third-party professionals, companies, or
              institutions, including accountants, lawyers, consultants, technology providers,
              banks, suppliers, investors, chambers, public institutions, development agencies, or
              advisory firms. Unless a specific written agreement states otherwise, Forealco is not
              responsible for the independent advice, acts, omissions, delays, fees, pricing,
              deliverables, decisions, or service quality of third-party providers.
            </SubBlock>

            <SubBlock title="3.6 Partner Fees, Referral Fees, and Commercial Benefits">
              Forealco may receive a referral fee, coordination fee, commission, success fee, or
              other commercial benefit from a third-party provider, seller, vendor, investor-side
              party, supplier, partner, or ecosystem participant where a business opportunity is
              introduced or coordinated. Where appropriate, Forealco aims to manage such
              relationships transparently and professionally. Users and clients should independently
              assess any third-party provider before entering into a separate engagement.
            </SubBlock>

            <SubBlock title="3.7 Intellectual Property">
              Unless otherwise stated, website content, structure, copy, design elements, original
              materials, brand names, and service descriptions are owned by or licensed to the
              relevant Erenown company. Third-party names, logos, trademarks, public institution
              names, or marks remain the property of their respective owners. Users may not copy,
              reproduce, modify, distribute, scrape, or commercially use website content without
              prior written consent.
            </SubBlock>

            <SubBlock title="3.8 External Links">
              This website may include links to third-party websites, public institutions, chambers,
              agencies, banks, technology providers, professional advisers, or other external
              resources. These links are provided for convenience and informational purposes only.
              Forealco does not control and is not responsible for the content, accuracy,
              availability, policies, or practices of external websites.
            </SubBlock>

            <SubBlock title="3.9 Acceptable Use">
              <p>You must not:</p>
              <Ul items={[
                "misuse the website",
                "interfere with its operation",
                "attempt unauthorised access",
                "upload malicious material",
                "scrape, copy, or reproduce content unlawfully",
                "use the website for unlawful, misleading, harmful, or abusive purposes",
              ]} />
            </SubBlock>

            <SubBlock title="3.10 Limitation of Liability">
              To the fullest extent permitted by applicable law, the relevant Erenown company
              disclaims liability for indirect, incidental, special, consequential, commercial,
              financial, regulatory, reputational, operational, or business loss arising out of
              website use or reliance on website content. Nothing in these Terms excludes liability
              that cannot lawfully be excluded.
            </SubBlock>

            <SubBlock title="3.11 No Contract Formed by Website Use Alone">
              Use of the website, a form submission, email communication, or a general inquiry does
              not by itself create a binding service contract, client relationship, legal advisory
              relationship, tax advisory relationship, accounting relationship, investment advisory
              relationship, partnership, agency relationship, or fiduciary duty. Any engagement is
              subject to a separate proposal, statement of work, engagement letter, contract, or
              other signed agreement.
            </SubBlock>

            <SubBlock title="3.12 Changes to the Website and These Terms">
              Forealco may update or discontinue content, links, service descriptions, resources,
              articles, or these Terms of Use from time to time.
            </SubBlock>

            <SubBlock title="3.13 Governing Framework">
              These Terms apply to website use only. Service-specific governing law, jurisdiction,
              entity identity, liability, fees, scope, deliverables, and responsibilities should be
              stated in the relevant commercial agreement, proposal, engagement letter, or legal
              notice.
            </SubBlock>
          </Section>

          <Section id="privacy" n="04" title="Privacy Policy">
            <p>
              This Privacy Policy explains how Forealco may collect, use, store, share, and protect
              personal data through this website and related communication channels.
            </p>

            <SubBlock title="4.1 Who Controls Personal Data?">
              <p>
                For general website operation, inquiries, inbound lead handling, business
                development requests, and contact form submissions, the primary data controller is
                proposed to be:
              </p>
              <p className="text-white/85">
                <strong>Erenown LLC</strong><br />
                Bulevardi Dëshmorët e Kombit, C2, HIII, Nr.6<br />
                Prishtinë, Republic of Kosovo<br />
                Registration number / unique identification number: 811869512
              </p>
              <p>
                Where a request is handled directly by the UK company, or where a specific
                opportunity, communication, or service is expressly transferred to the UK company,
                the relevant controller may instead be:
              </p>
              <p className="text-white/85">
                <strong>Erenown Ltd</strong><br />
                83 St. Marks Road, Enfield, England, EN1 1BJ<br />
                United Kingdom<br />
                Company number: 12845592
              </p>
              <p>
                General contact email for privacy and website matters:{" "}
                <a href={`mailto:${LEGAL_EMAIL}`} className="text-[#9DB4FF] hover:text-white">{LEGAL_EMAIL}</a>
              </p>
            </SubBlock>

            <SubBlock title="4.2 What Information Do We Collect?">
              <p>We may collect:</p>
              <Ul items={[
                "identity and contact details such as full name, company name, country, email address, phone number, and job title",
                "business inquiry details such as market-entry plans, Kosovo-related interests, company setup needs, partnership inquiries, institutional contacts, investment interests, advisory needs, procurement interests, supplier interests, or expansion plans",
                "project-related information that you voluntarily submit for research, assessment, or proposal preparation",
                "message content, attachments, technical or commercial details, and any information you choose to submit",
                "website usage data such as browser type, device information, IP-related technical logs, cookie preferences, and analytics data where enabled",
              ]} />
            </SubBlock>

            <SubBlock title="4.3 How We Collect Information">
              <p>We may collect information:</p>
              <Ul items={[
                "when you complete a website form",
                "when you request a call, meeting, or follow-up",
                "when you contact us by email or another communication channel",
                "when you communicate with us about Kosovo, market entry, business setup, advisory coordination, partnerships, institutions, research requests, project preparation, procurement, suppliers, or investment opportunities",
                "through cookies and similar technologies, subject to your preferences where required",
              ]} />
            </SubBlock>

            <SubBlock title="4.4 Why We Use Personal Data">
              <p>We may use personal data:</p>
              <Ul items={[
                "to respond to inquiries",
                "to assess whether Forealco, Erenown, or selected professional partners can assist you",
                "to arrange follow-up communication, meetings, introductions, or proposals",
                "to prepare research notes, project briefs, opportunity assessments, or other agreed deliverables",
                "to coordinate advisory, market-entry, procurement, supplier, investment, or business development discussions",
                "to manage inbound leads and institutional or partner contacts",
                "to improve website performance, security, usability, and communication flows",
                "to keep business records and maintain communication history",
                "to send relevant updates where permitted by law or where you have opted in",
                "to comply with applicable legal or regulatory obligations",
                "to protect legitimate business interests",
              ]} />
            </SubBlock>

            <SubBlock title="4.5 Legal Bases">
              <p>Depending on the nature of the interaction, personal data may be processed based on:</p>
              <Ul items={[
                "pre-contractual steps, where you ask us to assess or discuss a possible service",
                "performance of a contract, where a separate agreement is concluded",
                "legitimate interests, for the structured operation, improvement, protection, and development of our website and business communications",
                "consent, where required for non-essential cookies or optional marketing communications",
                "legal obligations, where retention or disclosure is required by applicable law",
              ]} />
            </SubBlock>

            <SubBlock title="4.6 Sharing of Data">
              <p>Personal data may be shared on a need-to-know basis with:</p>
              <Ul items={[
                "website hosting providers",
                "analytics providers",
                "email, CRM, or communication tools",
                "professional advisers, accountants, lawyers, consultants",
                "research or project contributors",
                "market-entry partners and technology providers",
                "suppliers or vendors, where relevant to your request",
                "banks or financial institutions, where relevant to your request",
                "investors or investment-related contacts, where relevant to your request",
                "chambers, agencies, public institutions, or business ecosystem contacts, where appropriate and lawful",
              ]} />
              <p>
                Data may also be shared between Erenown LLC and Erenown Ltd where necessary to
                assess, route, or perform a specific business opportunity, provided that the relevant
                operating or contracting entity is clearly identified when appropriate.
              </p>
              <p className="text-white/85"><strong>We do not sell personal data.</strong></p>
            </SubBlock>

            <SubBlock title="4.7 International Transfers">
              Because Forealco may support cross-border business coordination involving Kosovo,
              Türkiye, the United Kingdom, the European region, or other markets, personal data may
              be accessed or processed across more than one jurisdiction. Where that occurs, we aim
              to apply appropriate contractual, organisational, and technical safeguards
              proportionate to the circumstances.
            </SubBlock>

            <SubBlock title="4.8 Retention">
              <p>We keep personal data only for as long as reasonably necessary to:</p>
              <Ul items={[
                "respond to your request",
                "maintain business records",
                "manage follow-up communication",
                "evaluate potential collaboration",
                "prepare or discuss project-based work",
                "comply with legal obligations",
                "resolve disputes",
                "protect legitimate business interests",
              ]} />
              <p>Where data is no longer required, it may be deleted, anonymised, or securely archived.</p>
            </SubBlock>

            <SubBlock title="4.9 Your Rights">
              <p>Depending on applicable law, you may request:</p>
              <Ul items={[
                "access to your personal data",
                "correction or update of inaccurate data",
                "deletion where retention is no longer necessary",
                "objection to certain processing",
                "withdrawal of consent where consent is the legal basis",
                "restriction or portability where applicable",
                "opt-out from marketing communications at any time",
              ]} />
              <p>
                To exercise a privacy right, contact:{" "}
                <a href={`mailto:${LEGAL_EMAIL}`} className="text-[#9DB4FF] hover:text-white">{LEGAL_EMAIL}</a>
              </p>
            </SubBlock>

            <SubBlock title="4.10 Cookies">
              Please read the <a href="#cookies" className="text-[#9DB4FF] hover:text-white">Cookie Policy</a> and
              use the site cookie preference controls for more information.
            </SubBlock>

            <SubBlock title="4.11 Security">
              We apply reasonable technical and organisational safeguards to protect personal data.
              However, no website, communication channel, or storage system can be guaranteed to be
              completely secure.
            </SubBlock>

            <SubBlock title="4.12 Third-Party Websites">
              Where our website links to third-party partners, public authorities, chambers,
              development agencies, banks, professional advisers, or external information sources,
              their privacy practices are governed by their own policies and notices.
            </SubBlock>

            <SubBlock title="4.13 Change of Operating Entity or Transfer of Website Operations">
              Website operations, records, communications, and related rights and obligations may in
              the future be transferred, assigned, or novated to Erenown Ltd or to another company
              under the same ultimate beneficial ownership and control, where commercially
              appropriate and legally permitted. If that occurs, this Privacy Policy will be
              updated, and the relevant operating entity will be clearly identified.
            </SubBlock>

            <SubBlock title="4.14 Contact">
              For privacy questions or complaints, contact:{" "}
              <a href={`mailto:${LEGAL_EMAIL}`} className="text-[#9DB4FF] hover:text-white">{LEGAL_EMAIL}</a>
            </SubBlock>
          </Section>

          <Section id="cookies" n="05" title="Cookie Policy">
            <p>This Cookie Policy explains how Forealco uses cookies and similar technologies on its website.</p>

            <SubBlock title="5.1 What Cookies Are">
              Cookies are small text files stored on your device by websites you visit. They help
              websites function, remember preferences, measure usage, and improve user experience.
            </SubBlock>

            <SubBlock title="5.2 Types of Cookies We May Use">
              <CookieType title="Strictly necessary cookies" body="Used to operate core site functions, security controls, forms, consent settings, and essential session behaviour." />
              <CookieType title="Analytics cookies" body="Used to understand how visitors interact with the site and how the site can be improved." />
              <CookieType title="Preference cookies" body="Used to remember settings such as language, region, or consent choices." />
              <CookieType title="Marketing cookies" body="Used only where such tools are actually enabled and only where consent is obtained if required." />
            </SubBlock>

            <SubBlock title="5.3 Legal Basis">
              <p>Strictly necessary cookies may be used based on legitimate interests in running the website.</p>
              <p>Non-essential cookies should only be activated in line with the visitor's consent preferences, where applicable law requires that.</p>
            </SubBlock>

            <SubBlock title="5.4 How to Manage Cookies">
              <p>
                Visitors should be able to accept all cookies, reject non-essential cookies, or
                manage preferences through the cookie banner and preference center. Visitors may
                also manage cookies through their browser settings.
              </p>
            </SubBlock>

            <SubBlock title="5.5 Third-Party Technologies">
              Some cookies may be set by analytics tools, embedded content providers, contact form
              tools, chat tools, map tools, video tools, or other service providers used on the
              website.
            </SubBlock>

            <SubBlock title="5.6 Updates">
              This Cookie Policy may be updated from time to time to reflect changes in law,
              technology, or site functionality.
            </SubBlock>

            <SubBlock title="5.7 Contact">
              For questions about cookies or consent preferences, contact:{" "}
              <a href={`mailto:${LEGAL_EMAIL}`} className="text-[#9DB4FF] hover:text-white">{LEGAL_EMAIL}</a>
            </SubBlock>
          </Section>

          <Section id="disclaimer" n="06" title="Disclaimer">
            <p>
              Forealco is a business information, market-entry, advisory coordination, research, and
              project development brand by Erenown.
            </p>
            <p>
              This website may publish information about Kosovo, company setup, taxation, business
              environment, institutional structures, chambers, development agencies, advisory
              services, market opportunities, public procurement, banking, financial infrastructure,
              supplier networks, investment context, or related commercial topics.
            </p>
            <p>Nothing on this website should be interpreted as:</p>
            <Ul items={[
              "legal advice",
              "tax advice",
              "accounting advice",
              "regulatory assurance",
              "immigration advice",
              "financial advice",
              "investment advice",
              "banking advice",
              "public procurement advice",
              "or a guarantee of registration, approval, licensing, financing, market entry, partnership, contract award, investment, supplier acceptance, buyer acceptance, or business success",
            ]} />
            <p>
              Any Kosovo-related market-entry route, institutional reference, advisory pathway,
              partner introduction, research note, project preparation, supplier referral,
              investment discussion, or setup process described on the website remains subject to
              separate review, jurisdiction-specific requirements, partner availability, public
              authority procedures, and separate agreement where applicable.
            </p>

            <SubBlock title="External Links Disclaimer">
              <p>
                This website may contain links to external websites, including public institutions,
                chambers of commerce, development agencies, business organizations, banks, technology
                providers, professional advisers, suppliers, investors, or other third-party
                resources. These links are provided only for informational convenience.
              </p>
              <p>Forealco does not control, endorse, guarantee, or accept responsibility for:</p>
              <Ul items={[
                "the accuracy of external content",
                "the availability of third-party websites",
                "third-party privacy or cookie policies",
                "institutional procedure changes",
                "external service quality",
                "third-party representations or statements",
                "changes in laws, regulations, fees, or public authority requirements",
              ]} />
              <p>Users should independently verify information obtained from external websites.</p>
            </SubBlock>
          </Section>

          <Section id="partners" n="07" title="Partner & Referral Disclosure">
            <p>
              Forealco operates through an ecosystem-oriented model and may refer inquiries to
              selected partners, advisers, accountants, lawyers, consultants, software providers,
              banks, institutions, investors, suppliers, chambers, agencies, or other ecosystem
              participants where relevant.
            </p>
            <p>
              In some cases, the relevant Erenown company may receive a referral fee, coordination
              fee, commission, success fee, seller-side fee, or other commercial benefit from
              partner-related work.
            </p>
            <p>
              Forealco may also support business development processes where the commercial benefit
              is paid by the provider, seller, vendor, investor-side party, supplier, or partner
              rather than by the buyer or end client.
            </p>
            <p>
              Visitors and clients should independently assess any third-party provider before
              entering a separate engagement.
            </p>
            <p>
              Any third-party service remains subject to that provider's own terms, pricing, scope,
              professional responsibilities, confidentiality obligations, and compliance
              requirements.
            </p>
            <p>
              Unless expressly agreed otherwise in writing, Forealco is not responsible for the
              independent professional advice, acts, omissions, delays, deliverables, or fees of
              third-party providers.
            </p>
          </Section>

          <Section id="contact-form" n="08" title="Contact Form Notice">
            <p>
              By submitting a contact form on this website, you acknowledge that the information you
              provide may be processed by Forealco and Erenown LLC for the purpose of reviewing your
              inquiry and responding to your request.
            </p>
            <p>
              Depending on the nature of your inquiry, your information may be shared with relevant
              professional partners, consultants, accountants, legal advisors, technology providers,
              banks, institutions, investors, suppliers, or business contacts where necessary to
              evaluate or respond to your request.
            </p>
            <p>
              Please do not submit confidential, sensitive, or legally privileged information
              through the contact form unless specifically requested through an appropriate
              communication channel.
            </p>
            <p>
              Submitting a form does not create a client relationship, advisory engagement, legal
              relationship, tax advisory relationship, investment advisory relationship,
              partnership, agency relationship, fiduciary duty, or contractual obligation unless a
              separate written agreement is concluded.
            </p>

            <Callout label="CONSENT TEXT — INQUIRY">
              I confirm that I have read and understood the Privacy Policy and agree that my
              information may be processed for the purpose of responding to my inquiry.
            </Callout>

            <Callout label="OPTIONAL — BUSINESS COORDINATION">
              I agree that Forealco may share my inquiry details with relevant professional partners
              where necessary to evaluate or respond to my request.
            </Callout>
          </Section>

          <Section id="marketing" n="09" title="Marketing Consent">
            <p>
              A separate optional checkbox is used for newsletter, updates, articles, or business
              briefings.
            </p>
            <Callout label="CONSENT TEXT — MARKETING (OPTIONAL)">
              I would like to receive occasional updates about Forealco articles, Kosovo business
              insights, market-entry topics, advisory resources, project opportunities, and
              partnership opportunities. I understand that I can unsubscribe at any time.
            </Callout>
          </Section>

          <Section id="security" n="10" title="Security / Responsible Disclosure">
            <p>
              If you believe you have identified a security issue affecting the Forealco website,
              please contact{" "}
              <a href={`mailto:${LEGAL_EMAIL}`} className="text-[#9DB4FF] hover:text-white">{LEGAL_EMAIL}</a>{" "}
              with relevant details.
            </p>
            <p>Please:</p>
            <Ul items={[
              "do not exploit a vulnerability",
              "do not access data without authorisation",
              "do not disrupt services or impair availability",
              "do not conduct harmful, unlawful, or destructive testing",
              "do not disclose the issue publicly before Forealco has had a reasonable opportunity to review it",
            ]} />
            <p>Good-faith, responsible disclosure is appreciated.</p>
          </Section>

          <Section id="accessibility" n="11" title="Accessibility Statement">
            <p>
              Forealco aims to make its website readable, navigable, and usable for a broad range of
              visitors. We intend to improve content structure, responsive layout, form usability,
              clarity, and accessibility over time.
            </p>
            <p>
              If you encounter an accessibility issue, please contact{" "}
              <a href={`mailto:${LEGAL_EMAIL}`} className="text-[#9DB4FF] hover:text-white">{LEGAL_EMAIL}</a>.
            </p>
          </Section>

          {/* Footer note within Legal page */}
          <div className="border-t border-white/10 pt-8 text-[12px] leading-[1.7] text-white/45">
            © 2026 Forealco. A brand by Erenown. All rights reserved. Information on this website is
            provided for general business purposes and does not constitute professional advice.
          </div>
        </div>
      </section>

      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-24">
        <div className="border border-white/10 bg-white/[0.025] p-8 md:p-12 grid grid-cols-12 gap-6 items-center">
          <div className="col-span-12 md:col-span-8">
            <MonoLabel>QUESTIONS ABOUT THESE TERMS?</MonoLabel>
            <p className="mt-4 font-display text-[22px] md:text-[28px] leading-[1.35]">
              For privacy, legal, and compliance matters, write to{" "}
              <Em>{LEGAL_EMAIL}</Em>.
            </p>
          </div>
          <div className="col-span-12 md:col-span-4 md:text-right">
            <Link to="/explore-opportunities" className="btn-secondary">General contact →</Link>
          </div>
        </div>
      </section>
    </>
  );
}

/* === Hero === */
function Hero() {
  return (
    <section className="max-w-[1500px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20">
      <div className="fade-up"><MonoLabel>LEGAL & TRANSPARENCY</MonoLabel></div>
      <h1 className="h-display fade-up delay-1 mt-6 text-[44px] sm:text-[58px] lg:text-[68px] max-w-4xl">
        Legal & <Em>Privacy.</Em>
      </h1>
      <p className="fade-up delay-2 mt-7 max-w-2xl text-[14px] leading-[1.85] text-white/65">
        This page explains the legal, privacy, cookie, disclaimer, and transparency terms that apply
        to the Forealco website. Forealco is a business information, market-entry, advisory
        coordination, research, and project development brand by Erenown.
      </p>

      <div className="fade-up delay-3 mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl">
        <Meta label="EFFECTIVE DATE" value="1 January 2026" />
        <Meta label="LAST UPDATED" value="1 January 2026" />
        <Meta label="BRAND" value="Forealco" />
        <Meta label="LANGUAGE" value="English" />
      </div>
    </section>
  );
}

function Meta({ label, value }) {
  return (
    <div className="border-t border-white/10 pt-3">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#9DB4FF]">{label}</div>
      <div className="mt-1.5 text-[13px] text-white/85">{value}</div>
    </div>
  );
}

/* === Reusable === */
function Section({ id, n, title, children }) {
  return (
    <section id={id} data-testid={`legal-section-${id}`} className="scroll-mt-24">
      <div className="flex items-baseline gap-4 mb-6">
        <span className="font-mono text-[10px] tracking-[0.3em] text-[#9DB4FF]">{n}</span>
        <h2 className="h-display text-[28px] md:text-[36px] tracking-[-0.02em]">{title}</h2>
      </div>
      <div className="space-y-5 text-[14px] leading-[1.85] text-white/72 max-w-3xl">
        {children}
      </div>
    </section>
  );
}

function SubBlock({ title, children }) {
  return (
    <div className="mt-6">
      <div className="font-display text-[18px] md:text-[20px] text-white/95 mb-3">{title}</div>
      <div className="space-y-3">
        {typeof children === "string" ? <p>{children}</p> : children}
      </div>
    </div>
  );
}

function Ul({ items }) {
  return (
    <ul className="pl-5 space-y-1.5 text-white/65 list-none">
      {items.map((it) => (
        <li key={it} className="relative pl-4">
          <span className="absolute left-0 top-[0.7em] w-2 h-px bg-[#9DB4FF]/60" />
          {it}
        </li>
      ))}
    </ul>
  );
}

function CookieType({ title, body }) {
  return (
    <div className="border-l-2 border-[#9DB4FF]/40 pl-4 py-1 mb-4">
      <div className="text-white/95 text-[14px] font-medium">{title}</div>
      <div className="text-[13px] text-white/65 mt-1 leading-[1.7]">{body}</div>
    </div>
  );
}

function Callout({ label, children }) {
  return (
    <div className="my-5 border border-[#9DB4FF]/30 bg-[#9DB4FF]/[0.04] p-5">
      <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#9DB4FF] mb-3">{label}</div>
      <div className="text-[13.5px] leading-[1.7] text-white/85">{children}</div>
    </div>
  );
}
